/**
 * Local optimistic pending-transaction store.
 *
 * When a user submits a lock/burn we record it here immediately, so the
 * Transactions page can show it as pending during the window where the tx
 * exists on-chain but the API indexer hasn't created a record yet (the
 * pre-finality head scan runs on a ~30s poll; before it lands there is
 * nothing to show). Entries are pruned as soon as the API returns a record
 * with the same verified EVM event or unambiguous Casper source, or after
 * MAX_AGE_MS as a safety valve.
 */

import type { ApiTransaction, TransactionDirection } from 'types';
import { sourceEventKey } from './sourceEventIdentity';

const STORAGE_KEY = 'bridge.localPendingTxs';
/** Safety valve: drop local entries the API never claimed (e.g. tx reverted) */
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

export interface LocalPendingTx {
  /** Source-chain tx hash (EVM lock/burnWrapped or Casper deploy hash) */
  sourceTxHash: string;
  /** Stable identity for this local submission, including before its receipt exists. */
  localId?: string;
  evmChainId?: number | null;
  sourceEventIndex?: number | null;
  /** Exposure-control commitments: the transfer id its receipt proved (`0x` + 64 hex). */
  transferId?: string | null;
  direction: TransactionDirection;
  chainName: string;
  tokenSymbol: string;
  amount: string;
  sourceAddress: string;
  destinationAddress: string;
  createdAt: string; // ISO
}

type Listener = () => void;
const listeners = new Set<Listener>();

function emit(): void {
  listeners.forEach((l) => l());
}

/** Subscribe to store changes (for useSyncExternalStore) */
export function subscribeLocalPending(listener: Listener): () => void {
  listeners.add(listener);

  return () => listeners.delete(listener);
}

function readAll(): LocalPendingTx[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as LocalPendingTx[];

    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeAll(entries: LocalPendingTx[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // Storage full/unavailable — optimistic display is best-effort
  }
}

// Cache for getSnapshot: useSyncExternalStore requires referential stability
let snapshot: LocalPendingTx[] = readAll();

/** Current entries, pruned of expired ones (stable reference between changes) */
export function getLocalPendingSnapshot(): LocalPendingTx[] {
  return snapshot;
}

function refreshSnapshot(): void {
  const cutoff = Date.now() - MAX_AGE_MS;
  snapshot = readAll().filter((e) => new Date(e.createdAt).getTime() > cutoff);
}

/** Record a submission or enrich that exact local entry from its receipt. */
export function addLocalPending(entry: LocalPendingTx): void {
  const existing = readAll();
  const localIndex = entry.localId ? existing.findIndex(e => e.localId === entry.localId &&
    e.direction === entry.direction && e.chainName === entry.chainName && e.evmChainId === entry.evmChainId &&
    e.sourceTxHash.toLowerCase() === entry.sourceTxHash.toLowerCase()) : -1;
  if (localIndex >= 0) {
    // Repeated effects must not erase identity already learned from a receipt.
    existing[localIndex] = { ...entry, sourceEventIndex: entry.sourceEventIndex ?? existing[localIndex].sourceEventIndex };
    writeAll(existing);
  } else {
    const key = sourceEventKey(entry);
    if (key && existing.some(e => sourceEventKey(e) === key)) return;
    // Hash-only entries can be siblings. Give each submission its own local ID.
    writeAll([{ ...entry, localId: entry.localId ?? crypto.randomUUID() }, ...existing]);
  }
  refreshSnapshot();
  emit();
}

/** Used by both persistent reconciliation and the optimistic UI overlay. */
export function isPendingIndexed(entry: LocalPendingTx, apiTransactions: ApiTransaction[]): boolean {
  // Core's existing Casper representation is one source deploy/transaction
  // hash, not an EVM log or CES index. Only reconcile an unambiguous source
  // match in the same direction and EVM destination chain.
  if (entry.direction === 'casper_to_eth') {
    if (!entry.sourceTxHash.trim()) return false;
    const matches = apiTransactions.filter(tx =>
      tx.direction === entry.direction && (!tx.sourceChain || tx.sourceChain === 'casper') &&
      tx.sourceTxHash?.toLowerCase() === entry.sourceTxHash.toLowerCase() &&
      (entry.evmChainId != null && tx.evmChainId != null
        ? entry.evmChainId === tx.evmChainId
        : !!entry.chainName && entry.chainName === tx.chainName)
    );

    return new Set(matches.map(tx => tx.id)).size === 1;
  }
  const key = sourceEventKey(entry);

  return key !== null && apiTransactions.some(tx => sourceEventKey(tx) === key);
}

/** Drop matched source entries the API tracks, plus the existing expiry. */
export function reconcileLocalPending(apiTransactions: ApiTransaction[]): void {
  const cutoff = Date.now() - MAX_AGE_MS;
  const all = readAll();
  const kept = all.filter(e => !isPendingIndexed(e, apiTransactions) && new Date(e.createdAt).getTime() > cutoff);
  if (kept.length !== all.length) writeAll(kept);
  refreshSnapshot();
  emit();
}

/** Synthesize an ApiTransaction-shaped object for UI rendering */
export function toOptimisticApiTransaction(entry: LocalPendingTx): ApiTransaction {
  const isEthToCasper = entry.direction === 'eth_to_casper';

  return {
    id: `local-${entry.localId ?? sourceEventKey(entry) ?? JSON.stringify([entry.direction, entry.chainName, entry.sourceTxHash.toLowerCase(), entry.createdAt])}`,
    sourceTxHash: entry.sourceTxHash,
    evmChainId: entry.evmChainId,
    sourceEventIndex: entry.sourceEventIndex,
    direction: entry.direction,
    chainName: entry.chainName,
    tokenSymbol: entry.tokenSymbol,
    amount: entry.amount,
    sourceAddress: entry.sourceAddress,
    destinationAddress: entry.destinationAddress,
    status: 'pending',
    // Optimistic local entry: the indexer hasn't seen it yet, so the honest
    // raw status is 'confirming' (source tx sent, finality not yet proven).
    rawStatus: 'confirming',
    signaturesCollected: 0,
    signaturesRequired: 0,
    steps: isEthToCasper
      ? [
          { step: 'lock', status: 'completed', txHash: entry.sourceTxHash },
          { step: 'finality', status: 'active' },
          { step: 'attestation', status: 'pending' },
          { step: 'mint', status: 'pending' },
        ]
      : [
          { step: 'burn', status: 'completed', txHash: entry.sourceTxHash },
          { step: 'attestation', status: 'active' },
          { step: 'unlock', status: 'pending' },
        ],
    explorerLinks: {},
    createdAt: entry.createdAt,
    updatedAt: entry.createdAt,
  };
}
